/**
 * Welle 33 (2026-05-16) — Catalyst-Calendar Read-API.
 *
 * Liest v5_catalysts mit RLS (authenticated). Stellt 3 Standard-Queries bereit:
 *   - fetchUpcomingMarketCatalysts: marktweite Termine (FOMC/CPI/PCE/NFP)
 *   - fetchSymbolCatalysts: alle Termine eines Symbols inkl. Earnings
 *   - fetchUpcomingForSymbols: bulk fuer Watchlist/Positions-Liste
 */

import { supabase } from './supabase';

export type CatalystEventType =
  | 'earnings'
  | 'fomc'
  | 'cpi'
  | 'pce'
  | 'unemployment'
  | 'gdp'
  | 'analyst_day'
  | 'ipo_lockup'
  | 'dividend_ex'
  | 'stock_split'
  | 'product_launch'
  | 'other';

export type CatalystImpact = 'low' | 'medium' | 'high';
export type CatalystStatus = 'upcoming' | 'past' | 'cancelled';

export interface Catalyst {
  id: string;
  symbol: string | null;
  catalyst_date: string;
  catalyst_time: string | null;
  event_type: CatalystEventType;
  title: string;
  expected_impact: CatalystImpact;
  status: CatalystStatus;
  notes: string | null;
  source: string;
  source_url: string | null;
}

/** Marktweite Catalysts in den naechsten N Tagen */
export async function fetchUpcomingMarketCatalysts(
  daysAhead: number = 14,
  signal?: AbortSignal
): Promise<Catalyst[]> {
  const today = new Date().toISOString().slice(0, 10);
  const future = new Date(Date.now() + daysAhead * 86400000).toISOString().slice(0, 10);
  let q = supabase
    .from('v5_catalysts')
    .select('*')
    .is('symbol', null)
    .eq('status', 'upcoming')
    .gte('catalyst_date', today)
    .lte('catalyst_date', future)
    .order('catalyst_date', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw new Error(`fetchUpcomingMarketCatalysts: ${error.message}`);
  return (data ?? []) as Catalyst[];
}

/** Alle Catalysts eines Symbols in den naechsten N Tagen + marktweite */
export async function fetchSymbolCatalysts(
  symbol: string,
  daysAhead: number = 90,
  includeMarketWide: boolean = true,
  signal?: AbortSignal
): Promise<Catalyst[]> {
  const today = new Date().toISOString().slice(0, 10);
  const future = new Date(Date.now() + daysAhead * 86400000).toISOString().slice(0, 10);
  let q = supabase
    .from('v5_catalysts')
    .select('*')
    .eq('status', 'upcoming')
    .gte('catalyst_date', today)
    .lte('catalyst_date', future)
    .order('catalyst_date', { ascending: true });
  if (includeMarketWide) {
    q = q.or(`symbol.eq.${symbol.toUpperCase()},symbol.is.null`);
  } else {
    q = q.eq('symbol', symbol.toUpperCase());
  }
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw new Error(`fetchSymbolCatalysts: ${error.message}`);
  return (data ?? []) as Catalyst[];
}

/** Bulk: pro Symbol naechster Catalyst in den naechsten N Tagen */
export async function fetchUpcomingForSymbols(
  symbols: string[],
  daysAhead: number = 30,
  signal?: AbortSignal
): Promise<Map<string, Catalyst>> {
  if (symbols.length === 0) return new Map();
  const today = new Date().toISOString().slice(0, 10);
  const future = new Date(Date.now() + daysAhead * 86400000).toISOString().slice(0, 10);
  let q = supabase
    .from('v5_catalysts')
    .select('*')
    .in('symbol', symbols.map((s) => s.toUpperCase()))
    .eq('status', 'upcoming')
    .gte('catalyst_date', today)
    .lte('catalyst_date', future)
    .order('catalyst_date', { ascending: true });
  if (signal) q = q.abortSignal(signal);
  const { data, error } = await q;
  if (error) throw new Error(`fetchUpcomingForSymbols: ${error.message}`);
  const rows = (data ?? []) as Catalyst[];
  const out = new Map<string, Catalyst>();
  // Pro Symbol: ersten (naechsten) merken
  for (const r of rows) {
    if (!r.symbol) continue;
    if (!out.has(r.symbol)) out.set(r.symbol, r);
  }
  return out;
}

/** Catalysts deutsche Anzeigetitel */
export const EVENT_TYPE_LABEL: Record<CatalystEventType, string> = {
  earnings: 'Earnings',
  fomc: 'FOMC',
  cpi: 'CPI',
  pce: 'PCE',
  unemployment: 'NFP',
  gdp: 'BIP',
  analyst_day: 'Analyst-Day',
  ipo_lockup: 'IPO-Lockup-Ende',
  dividend_ex: 'Dividend Ex-Date',
  stock_split: 'Stock-Split',
  product_launch: 'Product-Launch',
  other: 'Sonstiges',
};

export function daysUntil(dateStr: string): number {
  const target = new Date(dateStr + 'T00:00:00Z').getTime();
  const today = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
  return Math.round((target - today) / 86400000);
}
